import React, { useState, useMemo, useEffect } from "react";
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox } from "lucide-react";
import { cn } from "../../lib/utils";

const PAGE_SIZES = [10, 20, 50];

const readPersisted = (key) => {
  if (!key) return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(key) || "{}") || {};
  } catch (_) {
    return {};
  }
};

export default function DataTable({
  columns,
  data = [],
  loading = false,
  pageSize: defaultPageSize = 10,
  searchable = true,
  persistKey,
  searchPlaceholder = "Buscar...",
  onRowClick,
  emptyMessage = "No hay datos para mostrar.",
  className,
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = readPersisted(persistKey).page;
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const s = readPersisted(persistKey).pageSize;
    return PAGE_SIZES.includes(s) ? s : defaultPageSize;
  });

  useEffect(() => {
    if (!persistKey) return;
    try {
      window.sessionStorage.setItem(
        persistKey,
        JSON.stringify({ page, pageSize }),
      );
    } catch (_) {}
  }, [persistKey, page, pageSize]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        if (!col.accessor) return false;
        const val = row[col.accessor];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Toolbar */}
      {searchable && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              className="w-full pl-9 pr-4 py-2 bg-white border border-brand-200 rounded-xl text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Filas por página:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-brand-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-brand-400 bg-white"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-brand-100 shadow-card">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-brand-gradient text-white">
              {columns.map((col, i) => (
                <th
                  key={i}
                  className={cn(
                    "px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider whitespace-nowrap",
                    col.headerClassName
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-brand-50">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-3 text-brand-400">
                    <div className="h-8 w-8 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin" />
                    <span className="text-sm">Cargando datos...</span>
                  </div>
                </td>
              </tr>
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <Inbox className="h-10 w-10 text-brand-200" />
                    <span className="text-sm">{emptyMessage}</span>
                    {search && (
                      <span className="text-xs text-gray-400">
                        Sin resultados para "{search}"
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "transition-colors duration-150",
                    onRowClick && "cursor-pointer hover:bg-brand-50",
                    rowIdx % 2 === 0 ? "bg-white" : "bg-brand-50/30"
                  )}
                >
                  {columns.map((col, colIdx) => (
                    <td
                      key={colIdx}
                      className={cn("px-4 py-3 text-gray-700 whitespace-nowrap", col.cellClassName)}
                    >
                      {col.render ? col.render(row[col.accessor], row) : (row[col.accessor] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-600">
          <span>
            {filtered.length === 0
              ? "Sin resultados"
              : `Mostrando ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} de ${filtered.length}`}
          </span>
          <div className="flex items-center gap-1">
            <PagBtn onClick={() => setPage(1)} disabled={safePage === 1} title="Primera">
              <ChevronsLeft className="h-4 w-4" />
            </PagBtn>
            <PagBtn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} title="Anterior">
              <ChevronLeft className="h-4 w-4" />
            </PagBtn>
            {pageNumbers(safePage, totalPages).map((p, i) =>
              p === "..." ? (
                <span key={i} className="px-2 text-gray-400">…</span>
              ) : (
                <button
                  key={i}
                  onClick={() => setPage(p)}
                  className={cn(
                    "h-8 w-8 rounded-lg text-sm font-medium transition-all",
                    p === safePage
                      ? "bg-brand-600 text-white shadow-sm"
                      : "text-gray-600 hover:bg-brand-100"
                  )}
                >
                  {p}
                </button>
              )
            )}
            <PagBtn onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} title="Siguiente">
              <ChevronRight className="h-4 w-4" />
            </PagBtn>
            <PagBtn onClick={() => setPage(totalPages)} disabled={safePage === totalPages} title="Última">
              <ChevronsRight className="h-4 w-4" />
            </PagBtn>
          </div>
        </div>
      )}
    </div>
  );
}

function PagBtn({ children, disabled, onClick, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-brand-100 disabled:opacity-40 disabled:pointer-events-none transition-all"
    >
      {children}
    </button>
  );
}

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
  if (current >= total - 3) return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
}
